import { HERO_VIEW }    from '../../../shared/constants/heroView.js'
import { HERO_OUTLINE } from '../../../shared/constants/heroOutline.js'

// ── Null object — returned before config is available ─────────────────────────

const NULL_CAPABILITIES = {
  availableViews:        [],
  buildingFrameStart:    0,
  buildingFramesCount:   72,
  buildingOutlineLayers: [],
  hasBuildingModel:      false,
  hasHeroSection:        false,
  hasSiteModel:          false,
  hasVisualization:      false,
  highlightSoldInRed:    false, //TODO: move this to AppCapabilites as its used by both Hero and Tiers domain
  initialOutline:        HERO_OUTLINE.BUILDINGS,
  initialView:           HERO_VIEW.VISUALIZATION,
  siteFrameStart:        0,
  siteFramesCount:       72,
  siteOutlineLayers:     [],
  visOutlineLayers:      [],
}

// ── Helper ─────────────────────────────────────────────────────────────────────

/**
 * Converts the `outlines_*` boolean flags in an `enabled_config_block` entry
 * to an ordered array of available HERO_OUTLINE values.
 *
 * @param {{ outlines_buildings?: boolean, outlines_tiers?: boolean, outlines_apartments?: boolean }} cfg
 * @returns {string[]}
 */
function outlineTypes(cfg) {
  const result = []
  if (cfg.outlines_buildings)  result.push(HERO_OUTLINE.BUILDINGS)
  if (cfg.outlines_tiers)      result.push(HERO_OUTLINE.TIERS)
  if (cfg.outlines_apartments) result.push(HERO_OUTLINE.APARTMENTS)
  return result
}

// ── Pure derivation ────────────────────────────────────

/**
 * Derives Hero section capabilities based on project configuration and panel settings.
 * Pure function — used by `useHeroCapabilities` and unit tests.
 *
 * @param {object|null} projectConfig - Configuration object from panel-config.json
 * @param {object|null} panelSettings - Configuration object from panel-settings.json
 * @returns {{
 *   availableViews:        string[],
 *   buildingFrameStart:    number,
 *   buildingFramesCount:   number,
 *   buildingOutlineLayers: string[],
 *   hasBuildingModel:      boolean,
 *   hasHeroSection:        boolean,
 *   hasSiteModel:          boolean,
 *   hasVisualization:      boolean,
 *   highlightSoldInRed:    boolean,
 *   initialOutline:        string,
 *   initialView:           string,
 *   siteFrameStart:        number,
 *   siteFramesCount:       number,
 *   siteOutlineLayers:     string[],
 *   visOutlineLayers:      string[],
 * }} Hero capabilities object
 */
export function deriveHeroCapabilities(projectConfig, panelSettings) {
  if (!projectConfig?.app_config) {
    return NULL_CAPABILITIES;
  }
  const appConfig = projectConfig.app_config ?? {}

  // ── Enabled config blocks ────────────────────────────────────────────────
  const siteRender         = appConfig.site?.render
  const siteRender360      = appConfig.site?.render_360
  const buildingsRender360 = appConfig.buildings?.render_360

  // First (and usually only) config entry per block
  const siteRenderCfg         = siteRender?.config?.[0]         ?? {}
  const siteRender360Cfg      = siteRender360?.config?.[0]      ?? {}
  const buildingsRender360Cfg = buildingsRender360?.config?.[0]  ?? {}

  const siteRenderGaussian = appConfig.site.gaussian_splat; //TODO: TEMP

  // ── Panel settings ───────────────────────────────────────────────────────
  const heroVisibility  = panelSettings.hero_visibility   ?? {}
  const modelLoadConfig = panelSettings.model_load_config ?? {}

  const renderHeroModule     = heroVisibility.render_hero_module    ?? true //TODO: move to appCapabilites and use form there
  const loadWithModel        = modelLoadConfig.load_with_model       ?? true
  const loadWithUnitOutlines = modelLoadConfig.load_with_unit_outlines ?? true
  const highlightSoldInRed   = modelLoadConfig.highlight_sold_in_red  ?? true //TODO: move this to AppCapabilites as its used by both Hero and Tiers domain

  // ── Available views ──────────────────────────────────────────────────────
  const hasVisualization = Boolean(siteRender?.enabled)
  const hasSiteModel     = Boolean(siteRender360?.enabled)
  const hasBuildingModel = Boolean(buildingsRender360?.enabled)

  const hasAnything    = hasVisualization || hasSiteModel || hasBuildingModel
  const hasHeroSection = renderHeroModule && hasAnything //TODO: export from appCapabilities?

  const availableViews = []
  if (hasVisualization) availableViews.push(HERO_VIEW.VISUALIZATION)
  if (hasSiteModel)     availableViews.push(HERO_VIEW.SITE)
  if (hasBuildingModel) availableViews.push(HERO_VIEW.BUILDING)

  // ── Frame counts ─────────────────────────────────────────────────────────
  const siteFramesCount    = siteRender360Cfg.frames      ?? 72
  const siteFrameStart     = siteRender360Cfg.frame_start ?? 0
  const buildingFramesCount = buildingsRender360Cfg.frames      ?? 72
  const buildingFrameStart  = buildingsRender360Cfg.frame_start ?? 0

  // ── Outline types per scope ──────────────────────────────────────────────
  const visOutlineLayers      = outlineTypes(siteRenderCfg)
  const siteOutlineLayers     = outlineTypes(siteRender360Cfg)
  const buildingOutlineLayers = outlineTypes(buildingsRender360Cfg)

  // ── Initial view ─────────────────────────────────────────────────────────
  // load_with_model=true → prefer site model, then building, then visualization
  let initialView
  if (loadWithModel && hasSiteModel)          initialView = HERO_VIEW.SITE
  else if (loadWithModel && hasBuildingModel) initialView = HERO_VIEW.BUILDING
  else if (hasVisualization)                  initialView = HERO_VIEW.VISUALIZATION
  else                                        initialView = availableViews[0] ?? HERO_VIEW.SITE

  // ── Initial outline ──────────────────────────────────────────────────────
  const initialOutline = loadWithUnitOutlines ? HERO_OUTLINE.APARTMENTS : HERO_OUTLINE.BUILDINGS

  return {
    availableViews,
    buildingFrameStart,
    buildingFramesCount,
    buildingOutlineLayers,
    hasBuildingModel,
    hasHeroSection,
    hasSiteModel,
    hasVisualization,
    highlightSoldInRed, //TODO: move this to AppCapabilites as its used by both Hero and Tiers domain
    initialOutline,
    initialView,
    siteFrameStart,
    siteFramesCount,
    siteOutlineLayers,
    siteRenderGaussian,
    visOutlineLayers,
  }
}